import { useEffect, useRef } from 'react';
import { Box, Group, Loader, Stack, Text } from '@mantine/core';
import type { ReactElement, Ref } from 'react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { ConversationViewModel } from './ConversationViewModel';
import { MarkdownBlock } from '@/design/MarkdownBlock';
import { accentTokens } from '@/design/VizAccents';
import { formatTimestamp, unwrapResponseText } from '@/shared/analytics/MetricFormatting';
import type { VizAccent } from '@/design/VizAccents';
import type { ConversationTurn } from '@/shared/analytics/AnalyticsTypes';

interface ConversationViewerProps {
    sessionId: string;
    focusTurnIndex: number;
    /** @deprecated Bubbles use the brand colour now; accent is ignored. */
    accent?: VizAccent;
    /**
     * Colours the focused-turn highlight ring. Defaults to the dedicated
     * `--bm-color-highlight` gold (deliberately not an accent) so the selection
     * stands out from the accent-coloured bubbles. Pass an accent to override
     * (e.g. governance keeps its rose ring while the bubbles use another accent).
     */
    highlightAccent?: VizAccent;
    /** Scroll the focused turn into view once loaded. Default true. */
    autoScroll?: boolean;
    /** Bump this value to scroll the focused turn into view on demand (e.g. a "jump" button). */
    scrollSignal?: number;
}

interface BubbleProps {
    role: 'prompt' | 'response';
    meta: string;
    text: string | null;
}

function ConversationBubble({ role, meta, text }: BubbleProps): ReactElement {
    const isPrompt = role === 'prompt';
    const displayText = isPrompt ? text : unwrapResponseText(text);
    return (
        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: isPrompt ? 'flex-end' : 'flex-start' }}>
            <Text c="dimmed" fw={700} tt="uppercase" mb={3} style={{ fontSize: '8px', letterSpacing: '0.08em' }}>
                {meta}
            </Text>
            <Box
                style={{
                    maxWidth: '90%',
                    padding: '8px 11px',
                    borderRadius: 'var(--bm-radius)',
                    background: isPrompt ? 'var(--bm-color-brand-soft)' : 'var(--bm-color-surface)',
                    border: `1px solid ${isPrompt ? 'var(--bm-color-brand-border)' : 'var(--bm-color-border-subtle)'}`,
                }}
            >
                {displayText === null || displayText.trim().length === 0 ?
                    <Text size="xs" c="dimmed" fs="italic">
                        (no text available)
                    </Text>
                : isPrompt ?
                    <Text size="xs" lh={1.55} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {displayText}
                    </Text>
                :   <MarkdownBlock>{displayText}</MarkdownBlock>}
            </Box>
        </Box>
    );
}

function TurnBlock({ turn, focused, highlightAccent, blockRef }: { turn: ConversationTurn; focused: boolean; highlightAccent?: VizAccent; blockRef?: Ref<HTMLDivElement> }): ReactElement {
    const ring =
        highlightAccent !== undefined ?
            { border: accentTokens(highlightAccent).solid, background: accentTokens(highlightAccent).soft }
        :   { border: 'var(--bm-color-highlight)', background: 'var(--bm-color-highlight-soft)' };
    return (
        <Box
            ref={blockRef}
            style={{
                padding: 10,
                borderRadius: 'var(--bm-radius)',
                border: focused ? `1px solid ${ring.border}` : '1px solid transparent',
                background: focused ? ring.background : undefined,
            }}
        >
            <Stack gap="sm">
                <ConversationBubble role="prompt" meta={`Prompt ${turn.turnIndex} · ${formatTimestamp(turn.timestamp)}`} text={turn.promptText} />
                <ConversationBubble role="response" meta={turn.model} text={turn.responseText} />
            </Stack>
        </Box>
    );
}

/** Renders a session's conversation as a prompt/response transcript, with the drilled-in turn highlighted; scrolls to it on load (unless `autoScroll` is false) or when `scrollSignal` changes. */
export default function ConversationViewer({ sessionId, focusTurnIndex, highlightAccent, autoScroll = true, scrollSignal = 0 }: ConversationViewerProps): ReactElement {
    const vm = useViewModel(ConversationViewModel);
    const focusEl = useRef<HTMLDivElement | null>(null);
    const assignFocus: Ref<HTMLDivElement> = (el) => {
        focusEl.current = el;
    };

    useEffect(() => {
        void vm.load(sessionId);
    }, [vm, sessionId]);

    useEffect(() => {
        if (autoScroll && !vm.loading && vm.turns.length > 0) focusEl.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [autoScroll, vm.loading, vm.turns, focusTurnIndex]);

    useEffect(() => {
        if (scrollSignal > 0) focusEl.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [scrollSignal]);

    if (vm.loading) {
        return (
            <Group justify="center" py="lg">
                <Loader size="sm" />
                <Text size="xs" c="dimmed">
                    Loading conversation…
                </Text>
            </Group>
        );
    }

    if (vm.errorMessage !== null) {
        return (
            <Text size="xs" c="red">
                {vm.errorMessage}
            </Text>
        );
    }

    if (vm.turns.length === 0) {
        return (
            <Text size="xs" c="dimmed" ta="center" py="md">
                No conversation transcript is available for this session.
            </Text>
        );
    }

    let focusAssigned = false;
    return (
        <Stack gap="xs">
            {vm.turns.map((turn) => {
                const focused = !focusAssigned && turn.turnIndex === focusTurnIndex;
                if (focused) focusAssigned = true;
                return <TurnBlock key={turn.requestId} turn={turn} focused={focused} highlightAccent={highlightAccent} blockRef={focused ? assignFocus : undefined} />;
            })}
        </Stack>
    );
}
